import React, { useEffect, useState } from 'react';
import { Star, Award } from 'lucide-react';
import { getSellerRatings, getMyRatingForSeller, submitRating } from '../../utils/api';
import { useAuth } from '../auth/AuthContext';

interface Props {
  sellerId: number;
  sellerName: string;
}

const StarRow = ({ value, onChange, readonly }: { value: number; onChange?: (v: number) => void; readonly?: boolean }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map(star => (
      <button
        key={star}
        type="button"
        onClick={() => onChange && onChange(star)}
        disabled={readonly}
        className={`transition-transform ${readonly ? 'cursor-default' : 'hover:scale-110 cursor-pointer'}`}
      >
        <Star
          className={`w-5 h-5 ${star <= value ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
        />
      </button>
    ))}
  </div>
);

const SellerRating = ({ sellerId, sellerName }: Props) => {
  const { user, isAuthenticated } = useAuth();
  const [average, setAverage] = useState<number | null>(null);
  const [count, setCount] = useState(0);
  const [reviews, setReviews] = useState<any[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const isOwnProduct = user && user.id === sellerId;

  useEffect(() => {
    getSellerRatings(sellerId).then(res => {
      if (res.success) {
        setAverage((res as any).average);
        setCount((res as any).count);
        setReviews((res as any).data || []);
      }
    });

    if (isAuthenticated && !isOwnProduct) {
      getMyRatingForSeller(sellerId).then(res => {
        if (res.success && res.data) {
          setMyRating(res.data.rating);
          setMyComment(res.data.comment || '');
          setSubmitted(true);
        }
      });
    }
  }, [sellerId, isAuthenticated, isOwnProduct]);

  const handleSubmit = async () => {
    if (!myRating) return;
    setSubmitting(true);
    const res = await submitRating(sellerId, myRating, myComment);
    if (res.success) {
      setSubmitted(true);
      setShowForm(false);
      // Bewertungen neu laden
      getSellerRatings(sellerId).then(r => {
        if (r.success) {
          setAverage((r as any).average);
          setCount((r as any).count);
          setReviews((r as any).data || []);
        }
      });
    }
    setSubmitting(false);
  };

  const visibleReviews = showAll ? reviews : reviews.slice(0, 3);

  return (
    <div className="mt-6 border-t pt-6">
      <h2 className="text-xl font-semibold mb-3">Verkäuferbewertungen</h2>

      {/* Durchschnitt + Top-Seller Badge */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        {average !== null ? (
          <>
            <StarRow value={Math.round(average)} readonly />
            <span className="font-bold text-lg text-gray-800">{average.toFixed(1)}</span>
            <span className="text-gray-500 text-sm">({count} {count === 1 ? 'Bewertung' : 'Bewertungen'})</span>
            {average >= 4.5 && count >= 3 && (
              <span className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full text-xs font-semibold">
                <Award className="w-3 h-3" /> Top Seller
              </span>
            )}
          </>
        ) : (
          <span className="text-gray-500 text-sm">Noch keine Bewertungen</span>
        )}
      </div>

      {/* Reviews */}
      {reviews.length > 0 && (
        <div className="space-y-3 mb-4">
          {visibleReviews.map((r, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <StarRow value={r.rating} readonly />
                <span className="text-sm font-medium text-gray-700">{r.rater_name}</span>
              </div>
              {r.comment && <p className="text-gray-600 text-sm">{r.comment}</p>}
            </div>
          ))}
          {reviews.length > 3 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="text-orange-500 text-sm font-medium hover:underline"
            >
              {showAll ? 'Weniger anzeigen' : `Alle ${reviews.length} Bewertungen anzeigen`}
            </button>
          )}
        </div>
      )}

      {/* Bewertungsformular */}
      {isAuthenticated && !isOwnProduct && (
        <div>
          {!showForm ? (
            <button
              onClick={() => setShowForm(true)}
              className="text-sm text-orange-500 font-medium hover:underline"
            >
              {submitted ? 'Bewertung bearbeiten' : 'Verkäufer bewerten'}
            </button>
          ) : (
            <div className="bg-orange-50 rounded-lg p-4 mt-2">
              <p className="font-medium text-gray-700 mb-2">
                {submitted ? 'Deine Bewertung bearbeiten' : `${sellerName} bewerten`}
              </p>
              <StarRow value={myRating} onChange={setMyRating} />
              <textarea
                value={myComment}
                onChange={e => setMyComment(e.target.value)}
                placeholder="Kommentar (optional)"
                rows={2}
                className="mt-3 w-full border rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
              />
              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !myRating}
                  className="bg-orange-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-orange-600 disabled:opacity-50 transition-colors"
                >
                  {submitting ? 'Speichern...' : 'Bewertung speichern'}
                </button>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-gray-500 text-sm hover:text-gray-700"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SellerRating;
